import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../../util/types';
import { convertNumberToMoney, formatMoney } from '../../../util/currency';
import { ensureListing, ensureUser } from '../../../util/data';
import { LISTING_TYPE } from '../../../constants/listingAttributes';
import { Avatar } from '../../../components';

import { CUSTOMER_FEE_PERCENTAGE } from '../../../transactions/transactionsUtil';
import { getCustomerFee, getNormalizedFee } from '../../../util/sessions';

import css from './SearchMapPriceLabel.module.css';

const SearchMapPriceLabel = props => {
  const {
    className,
    rootClassName,
    listing,
    isActive,
    onListingClicked,
    intl,
    currentUser,
  } = props;

  const classes = useMemo(() => classNames(rootClassName || css.root, className), [
    className,
    rootClassName,
  ]);
  const priceLabelClasses = useMemo(
    () => classNames(css.priceLabel, { [css.priceLabelActive]: isActive }),
    [isActive]
  );
  const caretClasses = useMemo(() => classNames(css.caret, { [css.caretActive]: isActive }), [
    isActive,
  ]);

  const currentListing = useMemo(() => ensureListing(listing), [listing]);

  // Create formatted price if currency is known or alternatively show just the unknown currency.
  const { price: listingPrice } = currentListing.attributes;

  const isCurrentUserAuthor = currentUser?.id?.uuid === currentListing?.author.id?.uuid;

  const buyerFee = isCurrentUserAuthor ? CUSTOMER_FEE_PERCENTAGE : getCustomerFee(currentUser);

  const feeAmount =
    listingPrice?.amount * (getNormalizedFee(buyerFee, CUSTOMER_FEE_PERCENTAGE) / 100);

  const price = useMemo(() => {
    return convertNumberToMoney(listingPrice?.amount + feeAmount, listingPrice?.currency);
  }, [feeAmount, listingPrice]);

  const formattedPrice = useMemo(
    () => (price && price.currency ? formatMoney(intl, price) : price.currency),
    [intl, price]
  );

  const isFree = useMemo(() => listing?.attributes?.publicData?.type === LISTING_TYPE.FREE, [
    listing,
  ]);

  const authorAvailable = currentListing && currentListing.author;
  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = currentAuthor ? ensureUser(currentAuthor) : null;

  // Get the smallest available image variant URL
  const getProfileImageUrl = (author) => {
    if (!author?.profileImage?.attributes?.variants) return null;
    
    const variants = author.profileImage.attributes.variants;
    
    // Try variants in order of preference for small sizes
    const preferredVariants = [
      'square-xsmall',    // 40x40
      'square-xsmall2x',  // 80x80
      'square-small',     // 240x240
    ];
    
    for (const variantName of preferredVariants) {
      if (variants[variantName]?.url) {
        return variants[variantName].url;
      }
    }
    
    // Fallback: use first available variant
    const firstVariant = Object.values(variants)[0];
    if (firstVariant?.url) {
      return firstVariant.url;
    }
    
    return null;
  };

  const profileImageUrl = ensuredAuthor ? getProfileImageUrl(ensuredAuthor) : null;

  return (
    <button className={classes} onClick={() => onListingClicked(currentListing)}>
      <div className={css.caretShadow} />
      <div className={priceLabelClasses}>
        {profileImageUrl && (
          <img 
            src={profileImageUrl} 
            alt={ensuredAuthor?.attributes?.profile?.displayName || ''} 
            className={css.avatar}
          />
        )}
        <span className={css.priceText}>
          {isFree ? <FormattedMessage id="General.free" /> : formattedPrice}
        </span>
      </div>
      <div className={caretClasses} />
    </button>
  );
};

SearchMapPriceLabel.defaultProps = {
  className: null,
  rootClassName: null,
};

const { func, string } = PropTypes;

SearchMapPriceLabel.propTypes = {
  className: string,
  rootClassName: string,
  listing: propTypes.listing.isRequired,
  onListingClicked: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

export default injectIntl(SearchMapPriceLabel);
